import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      try { if (data.access_token) localStorage.setItem("access_token", data.access_token); } catch {}
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      let msg;
      if (e?.response?.data?.detail) msg = formatApiErrorDetail(e.response.data.detail);
      else if (e?.code === "ERR_NETWORK" || e?.message === "Network Error") msg = "Server irraggiungibile. Controlla la connessione o riprova fra qualche secondo.";
      else if (e?.code === "ECONNABORTED") msg = "Il server è lento. Riprova.";
      else msg = e?.message || "Login non riuscito";
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    try { localStorage.removeItem("access_token"); } catch {}
    setUser(false);
  };

  const setUserAndToken = (u, token) => {
    try { if (token) localStorage.setItem("access_token", token); } catch {}
    if (u) setUser(u);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh: bootstrap, setUserAndToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
